// ============================================================================
// vehicleGolongan.js — Klasifikasi kendaraan 12 golongan Bina Marga (PUPR).
// ============================================================================
// Referensi: klasifikasi kendaraan Direktorat Jenderal Bina Marga, Kementerian
// PUPR (dipakai survei lalu lintas / LHR, dasar MKJI–PKJI).
//
// PENTING (batas teknis vision):
//   - Golongan 6-7 dibedakan dari JUMLAH SUMBU/GANDAR truk. YOLO/COCO hanya
//     mengenal kelas "truck" → jumlah sumbu TIDAK bisa dihitung dari tampak
//     depan. Untuk itu butuh tampak samping + deteksi sumbu, atau VLM.
//   - Maka classifyGolongan() memberi golongan TERBAIK dari data yang ada dan
//     menandai `estimated: true` + `basis` bila belum pasti. Jujur, bukan
//     mengarang angka sumbu.
// ============================================================================

// Tabel referensi 12 golongan (untuk UI/legend/laporan).
export const GOLONGAN_BINA_MARGA = [
  { gol: '1',  nama: 'Sepeda Motor',        deskripsi: 'Sepeda motor, sekuter, kendaraan roda 3' },
  { gol: '2',  nama: 'Sedan/Jeep',          deskripsi: 'Sedan, jeep, station wagon' },
  { gol: '3',  nama: 'Minibus/Combi',       deskripsi: 'Opelet, suburban, combi, minibus' },
  { gol: '4',  nama: 'Pick-up/Box',         deskripsi: 'Pick-up, micro truk, mobil hantaran (box)' },
  { gol: '5a', nama: 'Bus Kecil',           deskripsi: 'Bus kecil' },
  { gol: '5b', nama: 'Bus Besar',           deskripsi: 'Bus besar' },
  { gol: '6a', nama: 'Truk Ringan 2 Sumbu', deskripsi: 'Truk ringan 2 sumbu' },
  { gol: '6b', nama: 'Truk Sedang 2 Sumbu', deskripsi: 'Truk sedang 2 sumbu' },
  { gol: '7a', nama: 'Truk 3 Sumbu',        deskripsi: 'Truk 3 sumbu' },
  { gol: '7b', nama: 'Truk Gandengan',      deskripsi: 'Truk gandengan (trailer)' },
  { gol: '7c', nama: 'Truk Semi-Trailer',   deskripsi: 'Truk semi-trailer' },
  { gol: '8',  nama: 'Tidak Bermotor',      deskripsi: 'Sepeda, becak, dokar, andong' },
];

const GOL_INFO = Object.fromEntries(GOLONGAN_BINA_MARGA.map(g => [g.gol, g]));
export const golonganInfo = (gol) => GOL_INFO[gol] || null;

// ── Pemetaan kelas YOLO (COCO) → golongan Bina Marga ─────────────────────────
// Mengembalikan { gol, nama, estimated, basis, confidence }.
//   estimated=false hanya untuk pemetaan yang cukup andal (motor, sepeda).
//   Untuk mobil/bus/truk, sub-golongan adalah estimasi → estimated=true.
export function classifyGolongan(obj, frameH = 0) {
  const label = (obj?.label || '').toLowerCase();
  const conf = obj?.confidence ?? 0;
  const bbox = obj?.bbox;
  const boxH = Array.isArray(bbox) ? Math.max(0, bbox[3] - bbox[1]) : 0;
  // Rasio tinggi bbox terhadap tinggi frame — proxy "besar/kecil" (kasar,
  // dipengaruhi jarak kamera, jadi hanya untuk estimasi bus & mobil).
  const rel = frameH > 0 ? boxH / frameH : 0;

  switch (label) {
    case 'motorcycle':
      return { gol: '1', nama: GOL_INFO['1'].nama, estimated: false, basis: 'kelas YOLO', confidence: conf };

    case 'bicycle':
    case 'sepeda':                 // 'sepeda' = kelas D-FINE v2b (#1050)
      return { gol: '8', nama: GOL_INFO['8'].nama, estimated: false, basis: 'kelas model', confidence: conf };

    case 'becak':                  // kelas D-FINE v2b
      return { gol: '8', nama: GOL_INFO['8'].nama, estimated: false, basis: 'kelas model', confidence: conf };

    case 'gerobak':                // kelas D-FINE v2b (tak bermotor, dorong/tarik)
      return { gol: '8', nama: GOL_INFO['8'].nama, estimated: true, basis: 'kelas model', confidence: conf };

    case 'roda_tiga':              // kelas D-FINE v2b (bajaj/Viar/Kaisar)
      return { gol: '1', nama: GOL_INFO['1'].nama, estimated: false, basis: 'kelas model', confidence: conf };

    case 'angkot':                 // kelas D-FINE v2b (mikrolet); gol 5a Bus Kecil
      return { gol: '5a', nama: GOL_INFO['5a'].nama, estimated: true, basis: 'kelas model (perkiraan)', confidence: conf };

    case 'car': {
      // COCO "car" mencakup Gol 2/3/4. Tanpa model halus, default Gol 2 dan
      // tandai estimasi. Heuristik ringan: box relatif tinggi/kotak → minibus.
      const gol = rel > 0.42 ? '3' : '2';
      return { gol, nama: GOL_INFO[gol].nama, estimated: true, basis: 'kelas YOLO + ukuran (perkiraan)', confidence: conf };
    }

    case 'van':
      return { gol: '3', nama: GOL_INFO['3'].nama, estimated: true, basis: 'kelas YOLO (perkiraan)', confidence: conf };

    case 'bus': {
      const gol = rel > 0.5 ? '5b' : '5a';
      return { gol, nama: GOL_INFO[gol].nama, estimated: true, basis: 'kelas YOLO + ukuran (perkiraan)', confidence: conf };
    }

    case 'truck':
      // Sub-golongan (6a/6b/7a/7b/7c) butuh jumlah sumbu → TIDAK terdeteksi
      // dari tampak depan. Default estimasi truk 2 sumbu, jujur ditandai.
      return { gol: '6', nama: 'Truk (sumbu belum terdeteksi)', estimated: true, basis: 'kelas YOLO — jumlah sumbu perlu tampak samping/VLM', confidence: conf };

    default:
      return null;
  }
}

// Label ringkas untuk bbox: "GOL 2" atau "GOL 6*" (bintang = estimasi).
export function golonganBadge(g) {
  if (!g) return null;
  return `GOL ${g.gol}${g.estimated ? '*' : ''}`;
}
